// Design tokens for the Business Management System
// Single source of truth for color, spacing, radius, and typography.
//
// RULES (no exceptions):
// - Spacing: strict 8px grid. All padding/margin/gap values are multiples of 8
//   (8, 16, 24, 32). With MUI's default spacing unit of 8px this means sx values
//   of 1, 2, 3, 4 only. Form field vertical gap = 16px (gap: 2), section gap = 32px (gap: 4).
// - Radius: 6px for inputs/buttons/controls, 10px for cards/modals/drawers.
//   No pill shapes except the sidebar active state.
// - Color semantics: blue #1D4ED8 only for primary actions/links/active states.
//   Green #15803D = success only. Red #B91C1C = destructive/error only.
//   Amber #B45309 = warning only. KPI numbers are neutral gray-900 by default.

export const tokens = {
  color: {
    primary: '#1D4ED8',
    primaryDark: '#1E40AF',
    primaryLight: '#3B82F6',
    primarySurface: '#EFF6FF', // active-state background (e.g. sidebar selected)
    success: '#15803D',
    successSurface: '#F0FDF4',
    danger: '#B91C1C',
    dangerSurface: '#FEF2F2',
    warning: '#B45309',
    warningSurface: '#FFFBEB',
    // "info" is intentionally neutral: no decorative blue banners.
    info: '#4B5563',
    infoSurface: '#F3F4F6',
    neutral: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  radius: {
    control: '6px', // inputs, buttons, chips, menus
    surface: '10px', // cards, modals, drawers, papers
  },
  spacing: {
    unit: 8,
    field: 16, // vertical gap between form fields
    section: 32, // vertical gap between sections
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    weights: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    // The ONE label pattern: 13px / 500 / gray-700, rendered ABOVE the input.
    label: {
      fontSize: '13px',
      fontWeight: 500,
      color: '#374151', // neutral.700
    },
  },
};

// Convenience palette mapping for MUI createTheme
export const muiPalette = {
  primary: {
    main: tokens.color.primary,
    light: tokens.color.primaryLight,
    dark: tokens.color.primaryDark,
  },
  success: { main: tokens.color.success },
  warning: { main: tokens.color.warning },
  error: { main: tokens.color.danger },
  info: { main: tokens.color.info },
  grey: tokens.color.neutral,
};
